import type { AdvancedTableFilterOption, ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { numberUtils, useIsScreenDesktop } from '@zg-rentals/rental-manager-env';
import { Box, Heading, Icon, Text, Tooltip } from '@zillow/constellation-10';
import { IconQuestionMarkCircleFilled } from '@zillow/constellation-icons';
import React from 'react';
import { emitSetFilterEvent, EVENT_IDS } from '../../model/ClickstreamEvents';
import type { InvoiceItemFilters } from '../../model/InvoiceItemFilters';
import billingUtils from '../../utils/billingUtils';
import { calculateFilters } from './calculateFilters';
import { StyledContainer } from './StyledComponents';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraBillingInvoiceItemDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';

export interface InvoiceItemDetailsTableProps {
  currentInvoice: AdproductsZuoraBillingInvoiceDto;
}

export const InvoiceItemDetailsTable = ({ currentInvoice }: InvoiceItemDetailsTableProps) => {
  const isScreenDesktop = useIsScreenDesktop();
  const { user } = useRentalManagerUser();
  const [currentInvoiceLineItems, setCurrentInvoiceLineItems] = React.useState<
    Array<AdproductsZuoraBillingInvoiceItemDto>
  >([]);
  const [invoiceItemFilters, setInvoiceItemFitlers] = React.useState<InvoiceItemFilters>({});
  const isFlatFeeFeedConnect = billingUtils.isFlatFeeFeedConnectInvoice(currentInvoiceLineItems);

  React.useEffect(() => {
    if (currentInvoice) {
      const newFilters = calculateFilters(currentInvoice);
      setInvoiceItemFitlers(newFilters);
    }
  }, [currentInvoice]);

  const chargeItemTooltipMessage = isFlatFeeFeedConnect
    ? 'Billed a flat rate per month at the account level regardless of number of listings posted.'
    : 'Billed per published unit per day.';

  const invoiceColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceItemDto>> = [];
  if (currentInvoice.invoiceType === ZuoraInvoiceType.MULTIFAMILY) {
    invoiceColumnConfig.push({
      width: 4,
      header: 'Property',
      dataKey: 'propertyName',
      align: 'left',
    });
  }
  invoiceColumnConfig.push({
    width: isScreenDesktop ? 2 : 4,
    align: 'left',
    header: 'Charge Item',
    dataKey: 'chargeName',
    renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
      if (data.chargeName.includes('Zillow Feed Connect')) {
        return (
          <>
            <Tooltip
              useHoverProps={{
                delay: 100,
                restMs: 100,
              }}
              content={chargeItemTooltipMessage}
              shouldAwaitInteractionResponse
              trigger={
                <Text textStyle="body-xs" css={{ display: 'flex', alignItems: 'center' }}>
                  {data.chargeName}
                  <Icon size="sm" css={{ marginLeft: 3 }}>
                    <IconQuestionMarkCircleFilled />
                  </Icon>
                </Text>
              }
            />
          </>
        );
      }
      return data.chargeName;
    },
  });
  if (isScreenDesktop) {
    invoiceColumnConfig.push({
      width: 1,
      header: 'QTY',
      dataKey: 'quantity',
      align: 'right',
      renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
        const { quantity } = data;
        if (isNaN(quantity)) return '-';
        return quantity % 1 === 0 ? quantity : quantity.toFixed(2);
      },
    });
    invoiceColumnConfig.push({
      width: 2,
      header: 'Unit Price',
      customKey: 'unitPrice',
      renderer: (value: AdproductsZuoraBillingInvoiceItemDto) => {
        if (!value || !value.quantity) return '-';
        const formattedDiscount = billingUtils.formatDiscountRate(value);
        const formattedValue = numberUtils.asCurrencyWithDefault(value.chargeAmount / value.quantity, '-');
        return (
          <span>
            {formattedValue}
            {formattedDiscount && <div>{formattedDiscount}</div>}
          </span>
        );
      },
      align: 'right',
    });
  }
  invoiceColumnConfig.push({
    width: isScreenDesktop ? 2 : 3,
    dataKey: 'chargeAmount',
    header: 'AMOUNT',
    renderer: (value: AdproductsZuoraBillingInvoiceItemDto) => {
      const formattedValue = numberUtils.asCurrency(value.chargeAmount);
      const formattedDiscount = billingUtils.formatDiscountAmount(value);
      return (
        <span>
          {formattedValue}
          {formattedDiscount && <div>{formattedDiscount}</div>}
        </span>
      );
    },
    align: 'right',
  });

  React.useEffect(() => {
    if (!currentInvoice) return setCurrentInvoiceLineItems([]);
    if (!Object.keys(invoiceItemFilters).length) return setCurrentInvoiceLineItems(currentInvoice.zuoraInvoiceItems);
    let filteredItems = currentInvoice.zuoraInvoiceItems.slice();
    if (invoiceItemFilters.chargeName) {
      const filterSet = new Set();
      invoiceItemFilters.chargeName.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.chargeName));
      }
    }
    if (invoiceItemFilters.city) {
      const filterSet = new Set();
      invoiceItemFilters.city.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.city));
      }
    }
    if (invoiceItemFilters.propertyName) {
      const filterSet = new Set();
      invoiceItemFilters.propertyName.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.propertyName));
      }
    }
    if (invoiceItemFilters.state) {
      const filterSet = new Set();
      invoiceItemFilters.state.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.state));
      }
    }

    setCurrentInvoiceLineItems(filteredItems);
  }, [invoiceItemFilters, currentInvoice]);

  if (currentInvoice.invoiceType !== ZuoraInvoiceType.MULTIFAMILY) {
    // wait until surfaceing sfh boost order items before surfacing
    if (
      !currentInvoice.zuoraInvoiceItems
        .map((item) => item.chargeName.toLowerCase())
        .some((chargeName) => chargeName.includes('boost') || chargeName.includes('spotlight'))
    ) {
      return null;
    }
  }

  return (
    <StyledContainer data-eventid={EVENT_IDS.SET_FILTER} data-eventdescription="Set filter event">
      <Heading level={4}>Invoice details</Heading>
      <Box css={{ margin: 'xs' }} />
      <AdvancedTable
        onSaveFilters={(newFilters: InvoiceItemFilters) => {
          emitSetFilterEvent({ user });
          setInvoiceItemFitlers(newFilters);
        }}
        columnConfig={invoiceColumnConfig}
        data={currentInvoiceLineItems}
        filters={invoiceItemFilters}
        isMobile={!isScreenDesktop}
      />
      <Box css={{ paddingBottom: 'lg' }} />
    </StyledContainer>
  );
};
